import { useState } from 'react'
import {
  formatLkr,
  orderStatusStyles,
  paymentStatusStyles,
  stageStatusStyles,
  type LabOrderDetail,
} from './labOrderTypes'

export function LabStatusBadge({
  label,
  className,
}: {
  label: string
  className: string
}) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}
    >
      {label}
    </span>
  )
}

function InfoField({
  label,
  primary,
  secondary,
}: {
  label: string
  primary: string
  secondary?: string
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-slate-100">
        {primary}
      </p>
      {secondary ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">{secondary}</p>
      ) : null}
    </div>
  )
}

type Tab = 'status' | 'invoice'

export default function LabOrderDetailView({ order }: { order: LabOrderDetail }) {
  const [activeTab, setActiveTab] = useState<Tab>('status')
  const receivedPercent =
    order.finalTotalAmount > 0
      ? Math.round((order.totalReceived / order.finalTotalAmount) * 100)
      : 0

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Order #{order.id}
        </h3>
        <LabStatusBadge
          label={order.orderStatus}
          className={orderStatusStyles[order.orderStatus]}
        />
        <LabStatusBadge
          label={order.payment}
          className={paymentStatusStyles[order.payment]}
        />
        <span className="text-sm text-slate-500">
          {order.labName} · {order.finalTotal}
        </span>
      </div>

      <div className="grid gap-4 rounded-xl border border-slate-200 bg-slate-50/50 px-5 py-4 dark:border-slate-700 dark:bg-slate-800/40 md:grid-cols-2 lg:grid-cols-4">
        <InfoField
          label="Clinic"
          primary={order.clinic}
          secondary={order.clinicLocation}
        />
        <InfoField
          label="Patient"
          primary={order.patient}
          secondary={order.patientMeta || undefined}
        />
        <InfoField
          label="Case"
          primary={order.caseTitle}
          secondary={
            [order.caseMeta, order.caseDetail].filter(Boolean).join(' · ') ||
            undefined
          }
        />
        <InfoField
          label="Teeth"
          primary={
            Array.isArray(order.toothNos) && order.toothNos.length
              ? order.toothNos.join(', ')
              : '—'
          }
        />
        <InfoField
          label="Order date"
          primary={order.orderDate || '—'}
          secondary={order.dueDate ? `Due: ${order.dueDate}` : undefined}
        />
      </div>

      <div className="grid gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/80 md:grid-cols-2 lg:grid-cols-5">
        <InfoField label="Received by" primary={order.receivedBy} />
        <InfoField label="Received type" primary={order.receivedType} />
        <InfoField label="Delivery address" primary={order.deliveryAddress} />
        <InfoField label="Contact" primary={order.contact} />
        <InfoField label="Added by" primary={order.addedBy} />
      </div>

      {order.caseDetail && order.caseDetail !== '—' ? (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm dark:border-slate-700 dark:bg-slate-800/80">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Case description
          </p>
          <p className="mt-1 text-slate-800 dark:text-slate-200">{order.caseDetail}</p>
        </div>
      ) : null}

      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="-mb-px flex gap-6">
          {(['status', 'invoice'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={[
                'border-b-2 pb-3 text-sm font-medium capitalize transition-colors',
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              ].join(' ')}
            >
              {tab === 'status' ? 'Status & workflow' : 'Invoice & payments'}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'status' ? (
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
            <h4 className="mb-6 text-sm font-semibold text-slate-900 dark:text-slate-100">
              Workflow
            </h4>
            {order.workflow.length === 0 ? (
              <p className="text-sm text-slate-500">No workflow steps yet.</p>
            ) : (
              <ol className="relative space-y-0">
                {order.workflow.map((step, index) => {
                  const isLast = index === order.workflow.length - 1
                  return (
                    <li
                      key={`${step.title}-${index}`}
                      className="relative flex gap-4 pb-8 last:pb-0"
                    >
                      {!isLast && (
                        <span
                          className={[
                            'absolute left-[15px] top-8 h-[calc(100%-8px)] w-px',
                            step.state === 'completed'
                              ? 'bg-emerald-300'
                              : 'bg-slate-200',
                          ].join(' ')}
                          aria-hidden
                        />
                      )}
                      <div className="relative z-10 shrink-0">
                        {step.state === 'completed' && (
                          <div className="flex size-8 items-center justify-center rounded-full bg-emerald-500">
                            <svg
                              className="size-4 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m4.5 12.75 6 6 9-7.5"
                              />
                            </svg>
                          </div>
                        )}
                        {step.state === 'current' && (
                          <div className="flex size-8 items-center justify-center rounded-full border-2 border-amber-400 bg-white dark:bg-slate-800">
                            <span className="size-2.5 rounded-full bg-amber-400" />
                          </div>
                        )}
                        {step.state === 'upcoming' && (
                          <div className="flex size-8 items-center justify-center">
                            <span className="size-2.5 rounded-full bg-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 pt-0.5">
                        <p
                          className={[
                            'text-sm font-medium',
                            step.state === 'upcoming'
                              ? 'text-slate-400'
                              : 'text-slate-900 dark:text-slate-100',
                          ].join(' ')}
                        >
                          {step.title}
                        </p>
                        {step.timestamp ? (
                          <p className="mt-0.5 text-xs text-slate-500">
                            {step.timestamp}
                          </p>
                        ) : null}
                        {step.estimate ? (
                          <p className="mt-0.5 text-xs text-slate-400">
                            {step.estimate}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
            <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Stage log
              </h4>
            </div>
            {order.stageLog.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500">No stage entries yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-700">
                      <th className="px-4 py-3 font-medium text-slate-500">Date</th>
                      <th className="px-4 py-3 font-medium text-slate-500">Stage</th>
                      <th className="px-4 py-3 font-medium text-slate-500">Status</th>
                      <th className="px-4 py-3 font-medium text-slate-500">By</th>
                      <th className="px-4 py-3 font-medium text-slate-500">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.stageLog.map((entry, index) => (
                      <tr
                        key={index}
                        className="border-b border-slate-100 last:border-b-0 dark:border-slate-800"
                      >
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                          {entry.date}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                          {entry.stage}
                        </td>
                        <td className="px-4 py-3">
                          <LabStatusBadge
                            label={entry.status}
                            className={stageStatusStyles[entry.status]}
                          />
                        </td>
                        <td className="px-4 py-3 text-slate-600">{entry.by}</td>
                        <td className="px-4 py-3 text-slate-500">{entry.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
              <h4 className="mb-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
                Invoice summary
              </h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <dt>Net total</dt>
                  <dd>{formatLkr(order.netTotal)}</dd>
                </div>
                <div className="flex justify-between text-slate-600">
                  <dt>Discount</dt>
                  <dd>{formatLkr(order.generalDiscount)}</dd>
                </div>
                <div className="flex justify-between text-slate-600">
                  <dt>Delivery</dt>
                  <dd>{formatLkr(order.deliveryCharge)}</dd>
                </div>
                <div className="flex justify-between border-t border-slate-100 pt-3 dark:border-slate-700">
                  <dt className="font-semibold text-slate-900 dark:text-slate-100">
                    Final total
                  </dt>
                  <dd className="text-lg font-bold text-primary">
                    {formatLkr(order.finalTotalAmount)}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
              <p className="text-xs font-medium text-slate-500">Total received</p>
              <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">
                {formatLkr(order.totalReceived)}
              </p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${receivedPercent}%` }}
                />
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
              <p className="text-xs font-medium text-slate-500">Balance due</p>
              <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">
                {formatLkr(order.balanceDue)}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
              <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Lab items
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-700">
                      <th className="px-4 py-3 font-medium text-slate-500">#</th>
                      <th className="px-4 py-3 font-medium text-slate-500">Item</th>
                      <th className="px-4 py-3 font-medium text-slate-500">
                        Description
                      </th>
                      <th className="px-4 py-3 font-medium text-slate-500">Note</th>
                      <th className="px-4 py-3 font-medium text-slate-500">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.invoiceItems.map((line, index) => (
                      <tr
                        key={index}
                        className="border-b border-slate-100 last:border-b-0 dark:border-slate-800"
                      >
                        <td className="px-4 py-3 text-slate-500">{index + 1}</td>
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                          {line.item}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {line.description || '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {line.notes || '—'}
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {formatLkr(line.netPrice ?? line.price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {order.payments.length > 0 ? (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
                <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-700">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    Payments
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 font-medium text-slate-500">Date</th>
                        <th className="px-4 py-3 font-medium text-slate-500">Method</th>
                        <th className="px-4 py-3 font-medium text-slate-500">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.payments.map((p, index) => (
                        <tr key={index} className="border-b border-slate-100 last:border-b-0">
                          <td className="px-4 py-3">{p.date}</td>
                          <td className="px-4 py-3">{p.method}</td>
                          <td className="px-4 py-3 font-medium">
                            {formatLkr(p.total ?? p.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}
